import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { EASING, DURATION } from "@/lib/motionSystem";
import {
    Shield, Database, Eye, UserCheck, Lock, Globe, Mail,
    ChevronRight, ArrowLeft, ExternalLink, FileText,
    AlertCircle, CheckCircle2, Clock, Trash2, ChevronUp,
} from "lucide-react";

const LAST_UPDATED = "May 13, 2026";
const EFFECTIVE_DATE = "May 13, 2026";
const CONTACT_EMAIL = "privacy@genzdealz.ai";
const COMPANY_NAME = "GenZDealZ Technologies";
const APP_NAME = "GenZ Credit Score";
const APP_URL = "https://credit.genzdealz.ai";

const SECTIONS = [
    { id: "overview",       label: "Overview",                   icon: FileText      },
    { id: "data-collected", label: "Data We Collect",            icon: Database      },
    { id: "how-collected",  label: "How We Collect",             icon: Eye           },
    { id: "why-we-use",     label: "How We Use Your Data",       icon: CheckCircle2  },
    { id: "sharing",        label: "Data Sharing",               icon: Globe         },
    { id: "ai-processing",  label: "AI & Automated Processing",  icon: Shield        },
    { id: "retention",      label: "Data Retention",             icon: Clock         },
    { id: "your-rights",    label: "Your Rights",                icon: UserCheck     },
    { id: "consent",        label: "Consent & Withdrawal",       icon: CheckCircle2  },
    { id: "security",       label: "Security",                   icon: Lock          },
    { id: "third-party",    label: "Third-Party Services",       icon: ExternalLink  },
    { id: "contact",        label: "Contact & Grievances",       icon: Mail          },
];

// Animate once on mount — never whileInView (IntersectionObserver timing
// is unreliable for elements already visible on load).
function Section({ id, title, icon: Icon, iconColor = "text-blue-primary", idx = 0, children }) {
    return (
        <motion.div
            id={id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: DURATION.sectionReveal,
                delay: 0.08 + idx * 0.05,
                ease: EASING.smooth,
            }}
            className="premium-surface rounded-2xl border border-white/10 p-6 md:p-8 scroll-mt-28"
        >
            <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0">
                    <Icon size={17} className={iconColor} />
                </div>
                <h2 className="font-heading text-xl font-semibold text-text-primary">{title}</h2>
            </div>
            <div className="text-text-secondary text-sm leading-relaxed space-y-4">{children}</div>
        </motion.div>
    );
}

function Hi({ children }) {
    return <span className="text-text-primary font-medium">{children}</span>;
}

function Bullet({ items }) {
    return (
        <ul className="space-y-1.5">
            {items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-text-secondary">
                    <ChevronRight size={12} className="text-cyan-signal mt-0.5 shrink-0" />
                    <span>{item}</span>
                </li>
            ))}
        </ul>
    );
}

function DataBox({ label, items }) {
    return (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
            <p className="text-[10px] uppercase tracking-wider text-text-secondary/70 mb-2.5 font-semibold">{label}</p>
            <Bullet items={items} />
        </div>
    );
}

function Right({ title, desc }) {
    return (
        <div className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
            <CheckCircle2 size={14} className="text-green-pioneer mt-0.5 shrink-0" />
            <div>
                <p className="text-text-primary text-sm font-medium">{title}</p>
                <p className="text-text-secondary text-xs mt-0.5 leading-relaxed">{desc}</p>
            </div>
        </div>
    );
}

export default function PrivacyPolicy() {
    const [activeSec, setActiveSec] = useState("overview");
    const [atTop, setAtTop] = useState(true);

    useEffect(() => {
        const onScroll = () => {
            setAtTop(window.scrollY < 400);
            const found = SECTIONS.map((s) => {
                const el = document.getElementById(s.id);
                return el ? { id: s.id, top: el.getBoundingClientRect().top } : null;
            }).filter(Boolean).filter((o) => o.top <= 160);
            if (found.length) setActiveSec(found[found.length - 1].id);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

    return (
        <div className="min-h-dvh pt-24 pb-24">

            {/* ── Hero ─────────────────────────────────── */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20 pb-10"
            >
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary text-sm mb-8 transition-colors"
                >
                    <ArrowLeft size={14} /> Back to Home
                </Link>

                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-primary/15 border border-blue-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                        <Shield size={22} className="text-blue-primary" />
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-cyan-signal font-semibold mb-2">
                            Legal Document
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-text-primary mb-3">
                            Privacy Policy
                        </h1>
                        <p className="text-text-secondary text-base max-w-2xl leading-relaxed">
                            How <Hi>{APP_NAME}</Hi> collects, uses, and protects your personal data.
                            Committed to transparency and your rights under <Hi>DPDPA 2023</Hi>.
                        </p>
                        <div className="flex flex-wrap gap-3 mt-5">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-text-secondary text-xs">
                                <Clock size={11} /> Effective: {EFFECTIVE_DATE}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-text-secondary text-xs">
                                <Clock size={11} /> Updated: {LAST_UPDATED}
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-cyan-signal/25 bg-cyan-signal/5 text-cyan-signal text-xs">
                                <CheckCircle2 size={11} /> DPDPA 2023 Compliant
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ── Body: TOC + Content ──────────────────── */}
            <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20 flex flex-col lg:flex-row gap-8 items-start">

                {/* Sticky TOC — desktop */}
                <motion.aside
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: DURATION.sectionReveal, delay: 0.2, ease: EASING.smooth }}
                    className="hidden lg:block w-52 xl:w-60 shrink-0 sticky top-24"
                >
                    <div className="premium-surface rounded-2xl border border-white/10 p-4">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-text-secondary/60 font-semibold mb-3 px-1">
                            Contents
                        </p>
                        <nav className="space-y-0.5">
                            {SECTIONS.map((s) => {
                                const Ic = s.icon;
                                const active = activeSec === s.id;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => scrollTo(s.id)}
                                        className={`w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-all ${
                                            active
                                                ? "bg-blue-primary/15 text-blue-primary border border-blue-primary/20"
                                                : "text-text-secondary hover:text-text-primary hover:bg-white/[0.04]"
                                        }`}
                                    >
                                        <Ic size={11} className="shrink-0" />
                                        <span className="leading-tight">{s.label}</span>
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </motion.aside>

                {/* Mobile TOC */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: DURATION.pageTransition, ease: EASING.standard }}
                    className="lg:hidden w-full mb-6"
                >
                    <details className="premium-surface rounded-xl border border-white/10">
                        <summary className="px-4 py-3 text-sm text-text-secondary font-medium cursor-pointer flex items-center gap-2">
                            <FileText size={14} /> Jump to section
                        </summary>
                        <nav className="px-4 pb-4 grid grid-cols-2 gap-1">
                            {SECTIONS.map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => scrollTo(s.id)}
                                    className="text-left px-2.5 py-2 rounded-lg text-xs text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-all"
                                >
                                    {s.label}
                                </button>
                            ))}
                        </nav>
                    </details>
                </motion.div>

                {/* ── Sections ─────────────────────────── */}
                <div className="flex-1 min-w-0 space-y-5">

                    {/* 1. Overview */}
                    <Section id="overview" title="Overview" icon={FileText} idx={0}>
                        <p>
                            This Privacy Policy explains how <Hi>{COMPANY_NAME}</Hi> ("we", "us", "our")
                            collects, uses, stores, and protects your personal data when you use{" "}
                            <Hi>{APP_NAME}</Hi> at{" "}
                            <a href={APP_URL} className="text-cyan-signal hover:underline">{APP_URL}</a>.
                        </p>
                        <p>
                            {APP_NAME} is an alternative credit scoring platform for India's Gen Z that generates
                            a 300–900 credit score using six behavioural signals — academic performance, fee payment
                            history, developer activity, social presence, digital consumption, and data coverage —
                            as an alternative to traditional CIBIL scoring.
                        </p>
                        <div className="rounded-xl border border-amber-builder/25 bg-amber-builder/5 p-4 flex gap-3">
                            <AlertCircle size={15} className="text-amber-builder shrink-0 mt-0.5" />
                            <p className="text-amber-builder/90 text-sm">
                                By using {APP_NAME}, you agree to this Privacy Policy. You may withdraw consent at
                                any time from your account settings.
                            </p>
                        </div>
                        <p>
                            We act as the <Hi>Data Fiduciary</Hi> under India's Digital Personal Data Protection
                            Act, 2023 (DPDPA 2023), responsible for determining the purpose and means of processing
                            your personal data.
                        </p>
                    </Section>

                    {/* 2. Data Collected */}
                    <Section id="data-collected" title="Data We Collect" icon={Database} iconColor="text-purple-soft" idx={1}>
                        <p>We collect data across six categories depending on which signals you consent to share:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <DataBox label="Account & Identity" items={[
                                "Full name and email address",
                                "Password (bcrypt hash — never plaintext)",
                                "Profile picture URL (from OAuth providers)",
                                "Account creation and login timestamps",
                                "IP address and user-agent (brute-force protection only)",
                            ]} />
                            <DataBox label="Academic Signal" items={[
                                "Institution name and course category",
                                "Current academic year",
                                "CGPA / grades and grade scale",
                                "Attendance percentage",
                                "Grade trend (improving / stable / declining)",
                                "Uploaded academic documents (field extraction only)",
                            ]} />
                            <DataBox label="Fee Payment Signal" items={[
                                "Fee payment pattern (early / on-time / late / irregular)",
                                "Payment timing relative to due dates",
                                "Whether self-funded or loan-funded",
                                "Number of payment events (no bank details)",
                            ]} />
                            <DataBox label="Developer / Professional Signal" items={[
                                "GitHub username, public repo count, stars, followers",
                                "Programming languages (public repos only)",
                                "Recent commit activity (yes/no flag)",
                                "LinkedIn profile URL (publicly accessible only)",
                                "Portfolio URL (if provided)",
                            ]} />
                            <DataBox label="Social / Creator Signal" items={[
                                "YouTube subscribers, video count, views (Google OAuth)",
                                "Twitter/X follower count (URL analysis)",
                                "Instagram follower count (URL analysis)",
                                "Reddit karma score (URL analysis)",
                            ]} />
                            <DataBox label="Consumption Signal" items={[
                                "Monthly average spend (no transaction details)",
                                "Spending category breakdown (percentages only)",
                                "Six-month spend trend",
                                "Essential vs discretionary spend ratio",
                                "Note: Full e-commerce integration pending regulatory approval.",
                            ]} />
                        </div>
                        <p className="text-xs text-text-secondary/70">
                            We also collect <Hi>consent records</Hi> (scope grants/revocations with timestamps)
                            and <Hi>score records</Hi> including generated scores, AI explanations, and evidence
                            snapshots used at the time of scoring.
                        </p>
                    </Section>

                    {/* 3. How Collected */}
                    <Section id="how-collected" title="How We Collect Data" icon={Eye} iconColor="text-cyan-signal" idx={2}>
                        <p>We collect data through five mechanisms:</p>
                        <div className="space-y-3">
                            {[
                                {
                                    label: "Direct Input (Self-Declared)", badge: "Confidence: 30%",
                                    bc: "border-white/15 bg-white/[0.03]", tc: "text-text-secondary border-white/15",
                                    desc: "Data you manually enter during the onboarding wizard — CGPA, institution, fee patterns, social handles. You control exactly what you share.",
                                },
                                {
                                    label: "OAuth Authorisation", badge: "Confidence: 90%",
                                    bc: "border-green-pioneer/25 bg-green-pioneer/5", tc: "text-green-pioneer border-green-pioneer/30",
                                    desc: "When you connect GitHub or Google, we use OAuth 2.0 standard. You authorise access via the provider's consent screen. We only request the specific scopes listed below and store only fields relevant to credit scoring.",
                                },
                                {
                                    label: "Document Upload", badge: "Confidence: 85%",
                                    bc: "border-blue-primary/25 bg-blue-primary/5", tc: "text-blue-primary border-blue-primary/30",
                                    desc: "Academic transcripts or fee receipts you upload are processed to extract key fields (CGPA, payment amounts). Raw document files are not permanently stored in our database.",
                                },
                                {
                                    label: "Public URL Analysis", badge: "Confidence: 55%",
                                    bc: "border-cyan-signal/25 bg-cyan-signal/5", tc: "text-cyan-signal border-cyan-signal/30",
                                    desc: "If you provide a public profile URL (LinkedIn, Twitter, etc.), we extract publicly visible metrics. Only public data — no login or scraping of private content.",
                                },
                                {
                                    label: "Verified System (iWebTechno ERP)", badge: "Confidence: 95%",
                                    bc: "border-purple-soft/25 bg-purple-soft/5", tc: "text-purple-soft border-purple-soft/30",
                                    desc: "For partner institutions using iWebTechno's ERP system, we may receive verified academic and fee records with your explicit consent. Available only at participating campuses.",
                                },
                            ].map((m) => (
                                <div key={m.label} className={`rounded-xl border p-4 ${m.bc}`}>
                                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                        <p className="text-text-primary text-sm font-medium">{m.label}</p>
                                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${m.tc}`}>{m.badge}</span>
                                    </div>
                                    <p className="text-text-secondary text-xs leading-relaxed">{m.desc}</p>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* 4. Why We Use Data */}
                    <Section id="why-we-use" title="How We Use Your Data" icon={CheckCircle2} iconColor="text-green-pioneer" idx={3}>
                        <p>
                            We process your data only for the purposes you have explicitly consented to.
                            Each consent scope maps to a specific set of signals:
                        </p>
                        <div className="space-y-2">
                            {[
                                { scope: "academic_financial", color: "text-blue-primary", purpose: "Computing Academic and Fee Payment signals for your credit score." },
                                { scope: "developer_professional", color: "text-purple-soft", purpose: "Computing the Developer/Professional signal from GitHub, LinkedIn, and portfolio data." },
                                { scope: "social_creator", color: "text-amber-builder", purpose: "Computing the Social/Creator signal from YouTube, Twitter, Instagram, and Reddit." },
                                { scope: "identity_optional", color: "text-cyan-signal", purpose: "Verifying identity attributes to improve score confidence." },
                                { scope: "digilocker", color: "text-green-pioneer", purpose: "Verifying government-issued documents via DigiLocker (pending regulatory approval)." },
                                { scope: "ecom_consumption", color: "text-red-starter", purpose: "Computing the Consumption signal from e-commerce account data (pending)." },
                            ].map((s) => (
                                <div key={s.scope} className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]">
                                    <code className={`text-xs font-mono ${s.color} shrink-0 mt-0.5`}>{s.scope}</code>
                                    <p className="text-xs text-text-secondary">{s.purpose}</p>
                                </div>
                            ))}
                        </div>
                        <p>
                            We also use your data to: provide and maintain the service; prevent fraud and abuse;
                            improve our scoring model (aggregated, anonymised only); comply with legal obligations;
                            and respond to your requests. We do <Hi>not</Hi> use your data for advertising or sell
                            it to third-party marketers.
                        </p>
                    </Section>

                    {/* 5. Data Sharing */}
                    <Section id="sharing" title="Data Sharing" icon={Globe} iconColor="text-amber-builder" idx={4}>
                        <p>
                            We do <Hi>not</Hi> sell, rent, or trade your personal data. We share data only in these
                            limited circumstances:
                        </p>
                        <div className="space-y-3">
                            {[
                                { title: "Lender/NBFC Partners (with your explicit consent)", body: "When you choose to share your score with a lending partner, we provide a structured credit report: score, tier, signal breakdown, confidence level. Triggered only by your action. Raw evidence data is never shared." },
                                { title: "Anthropic (Claude API for AI explanations)", body: "After scoring, we send only the computed score result and anonymised signal summary to Claude to generate an explanation. No PII or raw personal data is sent to Anthropic." },
                                { title: "MongoDB Atlas (database hosting)", body: "All user and score data is stored in MongoDB Atlas, a managed cloud database. Governed by MongoDB's Privacy Policy." },
                                { title: "Legal obligations", body: "We may disclose data if required by Indian law, court order, or government authority, or to protect the rights, property, or safety of our users or the public." },
                                { title: "iWebTechno ERP (partner campuses only)", body: "With your prior consent, verified academic records flow from iWebTechno's ERP to improve score accuracy. No data flows back from your score computation." },
                            ].map((s) => (
                                <div key={s.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                                    <p className="text-text-primary text-sm font-medium mb-1.5">{s.title}</p>
                                    <p className="text-text-secondary text-xs leading-relaxed">{s.body}</p>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* 6. AI Processing */}
                    <Section id="ai-processing" title="AI & Automated Processing" icon={Shield} iconColor="text-purple-soft" idx={5}>
                        <p>
                            {APP_NAME} uses automated decision-making to compute your credit score. Here's how it
                            works and what rights you have:
                        </p>
                        <div className="rounded-xl border border-purple-soft/20 bg-purple-soft/5 p-5 space-y-4">
                            {[
                                { t: "Scoring Engine", b: "Your score (300–900) is computed by a deterministic weighted algorithm using 5–6 signal scores. Each signal has a documented weight (e.g., Academic 30%, Fee Payment 25%). No black-box ML — the formula is transparent and consistent." },
                                { t: "AI Explanation (Claude by Anthropic)", b: "After scoring, an AI explanation is generated using Anthropic's Claude model. Only your computed score result and a structured summary of signal weights are sent — not your raw personal data. The explanation does not change your score." },
                                { t: "Evidence Confidence Weighting", b: "Each evidence item carries a confidence weight: OAuth-verified (0.90), uploaded documents (0.85), URL-extracted (0.55), self-declared (0.30). Higher confidence evidence carries more weight in scoring." },
                                { t: "Your Right to Explanation", b: `Under DPDPA 2023, you have the right to a human-readable explanation of any automated decision. Every score generation includes a full signal breakdown and AI explanation in your score history. Contact ${CONTACT_EMAIL} for a detailed review.` },
                            ].map((item) => (
                                <div key={item.t}>
                                    <p className="text-text-primary text-sm font-medium mb-1">{item.t}</p>
                                    <p className="text-text-secondary text-xs leading-relaxed">{item.b}</p>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* 7. Retention */}
                    <Section id="retention" title="Data Retention" icon={Clock} iconColor="text-cyan-signal" idx={6}>
                        <p>
                            We retain your data only as long as necessary for the stated purposes or as required by law:
                        </p>
                        <div className="overflow-x-auto rounded-xl border border-white/10">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b border-white/10 bg-white/[0.03]">
                                        <th className="text-left py-2.5 px-4 text-text-secondary font-semibold uppercase tracking-wider">Data Type</th>
                                        <th className="text-left py-2.5 px-4 text-text-secondary font-semibold uppercase tracking-wider">Retention Period</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {[
                                        ["Account data", "Until account deletion + 30 days"],
                                        ["Score records", "Until account deletion"],
                                        ["Evidence data", "Until consent revoked or account deleted"],
                                        ["Consent audit log", "7 years (regulatory compliance)"],
                                        ["Login attempt logs", "15 minutes (rate-limiting only)"],
                                        ["AI explanations", "Stored with score records; deleted with account"],
                                        ["Uploaded documents", "Raw files not permanently stored"],
                                        ["Security & access logs", "90 days"],
                                    ].map(([type, period]) => (
                                        <tr key={type} className="hover:bg-white/[0.02] transition-colors">
                                            <td className="py-2.5 px-4 text-text-primary">{type}</td>
                                            <td className="py-2.5 px-4 text-text-secondary">{period}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p>
                            When you delete your account, cascade deletion across all collections occurs within 30 days.
                            Anonymised, aggregated data (no PII) may be retained for research purposes.
                        </p>
                    </Section>

                    {/* 8. Your Rights */}
                    <Section id="your-rights" title="Your Rights (DPDPA 2023)" icon={UserCheck} iconColor="text-green-pioneer" idx={7}>
                        <p>
                            Under the Digital Personal Data Protection Act, 2023, you have the following rights as a
                            Data Principal:
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Right title="Right to Information" desc="Know what personal data we hold and how it is used." />
                            <Right title="Right to Correction" desc="Correct inaccurate or incomplete personal data." />
                            <Right title="Right to Erasure" desc="Request deletion of your personal data within 30 days." />
                            <Right title="Right to Withdraw Consent" desc="Withdraw any consent scope at any time from Account Settings." />
                            <Right title="Right to Data Portability" desc="Export all your data in machine-readable JSON format." />
                            <Right title="Right to Grievance Redressal" desc="Lodge a complaint with our Grievance Officer." />
                            <Right title="Right to Explanation" desc="Request a human-readable explanation of automated score decisions." />
                            <Right title="Right to Nominate" desc="Nominate someone to exercise your data rights on your behalf." />
                        </div>
                        <div className="rounded-xl border border-blue-primary/20 bg-blue-primary/5 p-4">
                            <p className="text-text-primary text-sm font-medium mb-1">How to exercise your rights</p>
                            <p className="text-text-secondary text-xs leading-relaxed">
                                Most rights can be exercised directly from account settings. For data exports,
                                deletion, or complex queries, email{" "}
                                <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-signal hover:underline">{CONTACT_EMAIL}</a>.
                                We respond within <Hi>30 days</Hi>.
                            </p>
                        </div>
                    </Section>

                    {/* 9. Consent */}
                    <Section id="consent" title="Consent & Withdrawal" icon={CheckCircle2} iconColor="text-amber-builder" idx={8}>
                        <p>
                            {APP_NAME} is built on an explicit consent framework. Before collecting any signal data,
                            we present six consent scopes clearly. You choose which signals to share.
                        </p>
                        <p>
                            <Hi>You can withdraw consent at any time</Hi> from Account Settings → Consent Management.
                            Withdrawing a scope stops further data collection for that signal and removes existing
                            evidence from active scoring. Historical score records are retained unless you also
                            submit an erasure request.
                        </p>
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex gap-3">
                            <Trash2 size={15} className="text-red-starter shrink-0 mt-0.5" />
                            <p className="text-xs text-text-secondary leading-relaxed">
                                Withdrawing consent does not delete previously generated scores that used that data.
                                For full erasure of historical scores, submit an erasure request. Processed within
                                30 days.
                            </p>
                        </div>
                        <p>
                            Every consent grant and revocation is time-stamped and logged in an immutable audit
                            trail accessible to you and our compliance team.
                        </p>
                    </Section>

                    {/* 10. Security */}
                    <Section id="security" title="Security" icon={Lock} iconColor="text-blue-primary" idx={9}>
                        <p>We implement industry-standard security measures to protect your data:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[
                                { t: "Encryption at rest & transit", d: "All data encrypted using AES-256 at rest. All communications use TLS 1.3." },
                                { t: "Password hashing", d: "Passwords stored as bcrypt hashes with per-user salts. Plaintext passwords never stored." },
                                { t: "JWT authentication", d: "Short-lived access tokens (24h) + secure refresh tokens (7d). Stored in httpOnly cookies to prevent XSS." },
                                { t: "Brute-force protection", d: "Login attempts rate-limited to 5 per IP per email. Exceeding triggers a 15-minute lockout." },
                                { t: "No plaintext credentials", d: "OAuth tokens stored as opaque references. API keys stored in environment variables, not code." },
                                { t: "Minimal OAuth scopes", d: "We request only the scopes required for scoring. We never request write access to your accounts." },
                            ].map((s) => (
                                <div key={s.t} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                                    <p className="text-text-primary text-xs font-semibold mb-1 flex items-center gap-2">
                                        <Lock size={10} className="text-blue-primary" /> {s.t}
                                    </p>
                                    <p className="text-text-secondary text-xs leading-relaxed">{s.d}</p>
                                </div>
                            ))}
                        </div>
                        <p>
                            Despite these measures, no system is 100% secure. To report a security vulnerability,
                            email{" "}
                            <a href="mailto:security@genzdealz.ai" className="text-cyan-signal hover:underline">security@genzdealz.ai</a>.
                        </p>
                    </Section>

                    {/* 11. Third-Party */}
                    <Section id="third-party" title="Third-Party Services" icon={ExternalLink} iconColor="text-cyan-signal" idx={10}>
                        <p>We integrate with the following third-party services, each subject to its own privacy policy:</p>
                        <div className="space-y-3">
                            {[
                                { name: "GitHub (OAuth)", scopes: "read:user, user:email", purpose: "Authentication + public repo data for Developer signal", link: "https://docs.github.com/en/site-policy/privacy-policies/github-privacy-statement" },
                                { name: "Google (OAuth)", scopes: "openid, profile, email, youtube.readonly", purpose: "Authentication + YouTube channel data for Social signal", link: "https://policies.google.com/privacy" },
                                { name: "Anthropic Claude API", scopes: "API access (no PII sent)", purpose: "AI-generated score explanations and Vibe chat", link: "https://www.anthropic.com/privacy" },
                                { name: "MongoDB Atlas", scopes: "Data storage and retrieval", purpose: "Cloud database hosting for all user and score data", link: "https://www.mongodb.com/legal/privacy-policy" },
                                { name: "iWebTechno ERP (optional)", scopes: "Academic records (with consent)", purpose: "Verified academic/fee data for partner campuses", link: "https://iwebtechno.com" },
                            ].map((s) => (
                                <div key={s.name} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                                        <p className="text-text-primary text-sm font-medium">{s.name}</p>
                                        <a href={s.link} target="_blank" rel="noopener noreferrer"
                                            className="text-[10px] text-cyan-signal hover:underline inline-flex items-center gap-1">
                                            Privacy Policy <ExternalLink size={9} />
                                        </a>
                                    </div>
                                    <p className="text-text-secondary text-xs mb-2">{s.purpose}</p>
                                    <code className="text-[10px] px-2 py-0.5 rounded-md border border-white/10 bg-white/[0.04] text-text-secondary font-mono">{s.scopes}</code>
                                </div>
                            ))}
                        </div>
                        <p>We do not use advertising networks, social media pixels, or behavioural analytics platforms. We use no third-party cookies.</p>
                    </Section>

                    {/* 12. Contact */}
                    <Section id="contact" title="Contact & Grievances" icon={Mail} iconColor="text-green-pioneer" idx={11}>
                        <p>
                            For privacy questions, to exercise your rights, or to file a grievance:
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="rounded-xl border border-green-pioneer/20 bg-green-pioneer/5 p-5">
                                <p className="text-text-primary text-sm font-semibold mb-3">Data Fiduciary</p>
                                <p className="text-text-secondary text-xs leading-relaxed space-y-1">
                                    <strong className="text-text-primary block">{COMPANY_NAME}</strong>
                                    <span className="block">GenZDealZ.ai · India</span>
                                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-cyan-signal hover:underline block">{CONTACT_EMAIL}</a>
                                    <a href="https://genzdealz.ai" className="text-cyan-signal hover:underline block" target="_blank" rel="noopener noreferrer">genzdealz.ai</a>
                                </p>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                                <p className="text-text-primary text-sm font-semibold mb-3">Grievance Officer</p>
                                <p className="text-text-secondary text-xs leading-relaxed">
                                    Under Section 13 of DPDPA 2023:<br /><br />
                                    <strong className="text-text-primary">Email:</strong>{" "}
                                    <a href="mailto:grievance@genzdealz.ai" className="text-cyan-signal hover:underline">grievance@genzdealz.ai</a><br />
                                    <strong className="text-text-primary">Response SLA:</strong> 30 days<br />
                                    <strong className="text-text-primary">Escalation:</strong> Data Protection Board of India
                                </p>
                            </div>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                            <p className="text-text-primary text-sm font-medium mb-1">Policy Updates</p>
                            <p className="text-xs text-text-secondary leading-relaxed">
                                We may update this Privacy Policy as our product evolves or laws change. Significant
                                changes will be notified via email and a prominent notice at least 7 days before they
                                take effect. Continued use after changes constitutes acceptance.
                            </p>
                        </div>
                    </Section>

                    {/* Bottom nav */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: DURATION.sectionReveal, delay: 0.7 }}
                        className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 pb-2"
                    >
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 bg-white/[0.04] text-text-secondary hover:text-text-primary hover:bg-white/[0.08] text-sm font-medium transition-all"
                        >
                            <ArrowLeft size={14} /> Back to Home
                        </Link>
                        <p className="text-text-secondary/50 text-xs text-center">
                            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
                            <br />Last updated {LAST_UPDATED}
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Scroll-to-top button */}
            {!atTop && (
                <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="fixed bottom-28 right-4 md:right-6 z-40 w-10 h-10 rounded-full bg-blue-primary/80 hover:bg-blue-primary border border-white/20 flex items-center justify-center text-white shadow-lg transition-all"
                    aria-label="Scroll to top"
                >
                    <ChevronUp size={18} />
                </motion.button>
            )}
        </div>
    );
}
